import React, { useEffect, useState } from "react";
import { BRAND_NAME } from '../../../theme/brand';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Collapse,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  Button,
  Spinner,
  Flex,
  IconButton,
  Badge,
  Text,
  Select,
  Center,
  useToast,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { ArrowDownIcon, ArrowForwardIcon, ChevronDownIcon, SettingsIcon } from "@chakra-ui/icons";
import moment from "moment";
import Pagination from "react-js-pagination";
import RenderPropertiesWithToggle from "./JsonMode";
import TableFilters from "./Filters";
import API from "../../../helpers/axiosInstance";
import { getUserTimeZone } from "../../../utils/DefaultFunction";
import BoxWrapper from "../../../components/BoxWrapper/BoxWrapper";
import usePagePermissions from "../../../hooks/usePagePermissions";
import { LuActivity } from 'react-icons/lu';
import PageHeading from '../../../components/shared/PageHeading';

const EventsTable = () => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({
    total_events: 0,
    current_page: 1,
    total_pages: 1,
    per_page: 50,
  });
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("YTD");
  const [dateRange, setDateRange] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [uniqueEvents, setUniqueEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState("");
  const toast = useToast();
  const [visibleColumns, setVisibleColumns] = useState([
    "event_name",
    "session_distinct",
    "timestamp",
    "status",
    "drop_down",
    "contact",
    "country",
    "platform"
  ]);
  const { loading: permissionsLoading, can } = usePagePermissions('marketing/event');

  useEffect(() => {
    if (permissionsLoading || !can('event-list')) return;
    fetchUniqueEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsLoading]);

  const fetchUniqueEvents = async () => {
    setLoadingEvents(true);
    try {
      const response = await API.post(
        `${process.env.REACT_APP_API_URL}/fetch-unique-events`,
        {},
        { headers: getApiHeaders() }
      );
      if (response.data.status) {
        setUniqueEvents(response.data.data.events);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch events",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching unique events:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch events";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingEvents(false);
    }
  };

  const listPermission = can("event-list")
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const allColumns = [
    { key: "drop_down", label: "", isToggleBtn: true },
    { key: "event_name", label: "Event Name", isToggleBtn: false },
    { key: "session_distinct", label: "Event ID / Session ID / Distinct ID", isToggleBtn: false },
    { key: "platform", label: "Platform", isToggleBtn: false },
    { key: "country", label: "Country", isToggleBtn: false },
    { key: "contact", label: "Contact", isToggleBtn: false },
    { key: "timestamp", label: "Timestamp", isToggleBtn: false },

    { key: "ip_address", label: "IP Address", isToggleBtn: false },
    { key: "channel", label: "Channel", isToggleBtn: false },
    // { key: "status", label: "Status", isToggleBtn: false },

  ];

  const toggleRow = (eventId) => {
    const newExpanded = new Set(expandedRows);
    newExpanded.has(eventId) ? newExpanded.delete(eventId) : newExpanded.add(eventId);
    setExpandedRows(newExpanded);
  };

  const handleToggleColumn = (colKey) => {
    setVisibleColumns((prev) =>
      prev.includes(colKey) ? prev.filter((c) => c !== colKey) : [...prev, colKey]
    );
  };

  const getApiHeaders = () => ({
    "x-api-key": localStorage.getItem("api-key"),
    "x-access-token": localStorage.getItem("customer-portal"),
  });

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append("page", pagination.current_page);
    params.append("limit", pagination.per_page);
    params.append("tz", getUserTimeZone());

    if (searchQuery) params.append("search_term", searchQuery);
    if (selectedEvent) params.append("event_name", selectedEvent);

    if (dateRange && dateRange[0]) {
      params.append("date_from", moment(dateRange[0].startDate).format("YYYY-MM-DD"));
      params.append("date_to", moment(dateRange[0].endDate).format("YYYY-MM-DD"));
    }

    return params.toString();
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/fetch-events-portal?${buildQueryParams()}`;
      const response = await API.get(url, { headers: getApiHeaders() });

      if (response.data.success) {
        setEvents(response.data.data.events);
        setPagination((prev) => ({
          ...prev,
          ...response.data.data.pagination,
        }));
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setPagination((prev) => ({ ...prev, current_page: pageNumber }));
  };

  useEffect(() => {
    if (permissionsLoading || !can('event-list')) return;
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsLoading, activeFilter, dateRange, searchQuery, selectedEvent, pagination.current_page, pagination.per_page]);
  const renderValue = (value) =>
    value !== null && value !== undefined && value !== ""
      ? value
      : <Text color="gray.400" fontSize="sm">No data</Text>;
  return (
    <  >
      {permissionsLoading ? (
        <Center h="400px">
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Center>
      ) : !listPermission ? (
        <BoxWrapper>
          <Center h="400px">
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                No Access
              </Text>
              <Text fontSize="sm" color="gray.500">
                You don't have permission to view this sections.
              </Text>
            </Box>
          </Center>
        </BoxWrapper>
      ) : loading ? (
        <Center h="400px">
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Center>
      ) : <>

        <PageHeading icon={LuActivity} title="Events Tracking" subtitle={<>Showing {events?.length > 0 ? events.length : ""} most recent results of{" "}{pagination?.total_events} matches</>} mb={4}>
          {/* Rows per page beside Columns */}
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">
              Rows per page:
            </Text>
            <Box width="80px">
              <Select
                size="sm"
                value={pagination.per_page}
                onChange={(e) =>
                  setPagination((prev) => ({
                    ...prev,
                    per_page: Number(e.target.value),
                    current_page: 1,
                  }))
                }
              >
                {[10, 20, 50, 100].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </Select>
            </Box>
          </Flex>


          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              size="sm"
              rightIcon={<ChevronDownIcon />}
              leftIcon={<SettingsIcon />}
              border="1px"
              backgroundColor="white"
              borderColor="#0000001a"
            >
              Columns
            </MenuButton>
            <MenuList>
              {allColumns.map(
                (col) =>
                  col.key !== "drop_down" && (
                    <MenuItem key={col.key}>
                      <Checkbox
                        isChecked={visibleColumns.includes(col.key)}
                        onChange={() => handleToggleColumn(col.key)}
                      >
                        {col.label}
                      </Checkbox>
                    </MenuItem>
                  )
              )}
            </MenuList>
          </Menu>
        </PageHeading>


        <TableFilters
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          dateRange={dateRange}
          setDateRange={setDateRange}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          uniqueEvents={uniqueEvents}
          loadingEvents={loadingEvents}
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
        />

        {loading ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="xl" />
          </Flex>
        ) : (
          <>
            <Table variant="simple">
              <Thead>
                <Tr></Tr>
                <Tr>
                  {allColumns
                    .filter((col) => visibleColumns.includes(col.key))
                    .map((col) => (
                      <Th key={col.key}>{col.label}</Th>
                    ))}
                </Tr>
              </Thead>

              <Tbody>
                {/* ✅ Show 'No Data Found' if API failed or no data */}
                {!events || events.length <= 0 ? (
                  <Tr>
                    <Td colSpan={visibleColumns.length} textAlign="center" py={6}>
                      No Data Found
                    </Td>
                  </Tr>
                ) : (
                  events?.map((event, index) => (
                    <React.Fragment key={event.event_id || index}>
                      <Tr _hover={{ bg: "gray.100" }}>
                        {allColumns
                          .filter((col) => visibleColumns.includes(col.key))
                          .map((col) => (
                            <Td key={col.key} width={col.key === "drop_down" ? "0px" : ""}>
                              {col.isToggleBtn && (
                                <IconButton
                                  size="xs"
                                  variant="solid"
                                  aria-label="Toggle row"
                                  icon={
                                    expandedRows.has(event.event_id) ? (
                                      <ArrowDownIcon />
                                    ) : (
                                      <ArrowForwardIcon />
                                    )
                                  }
                                  onClick={() => toggleRow(event.event_id)}
                                />
                              )}

                              {col.key === "status" ? (
                                <Badge
                                  colorScheme={
                                    event.status?.toLowerCase() === "success"
                                      ? "green"
                                      : event.status?.toLowerCase() === "failed"
                                        ? "red"
                                        : event.status?.toLowerCase() === "pending"
                                          ? "yellow"
                                          : "gray"
                                  }
                                  px={3}
                                  py={1}
                                  borderRadius="full"
                                  fontSize="0.8em"
                                  textTransform="capitalize"
                                >
                                  {event.status || "N/A"}
                                </Badge>
                              ) : col.key === "timestamp" ? (
                                event.timestamp
                                  ? moment(event.timestamp).format("DD-MM-YYYY hh:mm:ss a")
                                  : <Text color="gray.400" fontSize="sm">N/A</Text>
                              ) : col.key === "event_name" ? (
                                renderValue(event.event_name)
                              ) : col.key === "session_distinct" ? (
                                <VStack align="stretch" spacing={1} py={1}>
                                  {[
                                    { label: "Event ID", value: event.event_id },
                                    { label: "Session ID", value: event.session_id },
                                    { label: "Distinct ID", value: event.distinct_id },
                                  ].map((item) => (
                                    <HStack key={item.label} spacing={2} align="flex-start">
                                      <Text
                                        fontSize="10px"
                                        fontWeight="700"
                                        color="gray.500"
                                        textTransform="uppercase"
                                        letterSpacing="0.03em"
                                        minW="76px"
                                        flexShrink={0}
                                      >
                                        {item.label}
                                      </Text>
                                      <Text
                                        fontSize="xs"
                                        fontFamily="mono"
                                        color={item.value ? "gray.700" : "gray.400"}
                                        wordBreak="break-all"
                                      >
                                        {renderValue(item.value)}
                                      </Text>
                                    </HStack>
                                  ))}
                                </VStack>
                              ) : !col.isToggleBtn ? (
                                event[col.key] !== null &&
                                  event[col.key] !== undefined &&
                                  event[col.key] !== ""
                                  ? event[col.key]
                                  : <Text color="gray.400" fontSize="sm">N/A</Text>
                              ) : null}
                            </Td>
                          ))}
                      </Tr>

                      <Tr>
                        <Td colSpan={visibleColumns.length} p={0} border="none">
                          <Collapse in={expandedRows.has(event.event_id)} animateOpacity>
                            <Box bg={"white"} p={6} borderTop="1px" borderColor={borderColor}>
                              <Tabs variant="enclosed">
                                <TabList
                                  bg="#f1f5f9"
                                  p="1"
                                  borderRadius="full"
                                  display="flex"
                                  justifyContent="space-between"
                                >
                                  <Tab
                                    flex="1"
                                    _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                                    borderRadius="full"
                                    fontSize="sm"
                                  >
                                    All Properties ({Object.keys(event.all_properties).length})
                                  </Tab>
                                  <Tab
                                    flex="1"
                                    _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                                    borderRadius="full"
                                    fontSize="sm"
                                  >
                                    Your Properties ({Object.keys(event.your_properties).length})
                                  </Tab>
                                  <Tab
                                    flex="1"
                                    _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                                    borderRadius="full"
                                    fontSize="sm"
                                  >
                                    {BRAND_NAME} Properties ({Object.keys(event.xnotify_properties).length})
                                  </Tab>
                                </TabList>

                                <TabPanels
                                  backgroundColor={"#f1f5f94d"}
                                  mt={4}
                                  border={"1px"}
                                  borderColor={"#0000001a"}
                                  borderRadius={"5px"}
                                >
                                  <TabPanel>
                                    <RenderPropertiesWithToggle
                                      title="All Properties"
                                      propsObj={event.all_properties}
                                    />
                                  </TabPanel>
                                  <TabPanel>
                                    <RenderPropertiesWithToggle
                                      title="Your Properties"
                                      propsObj={event.your_properties}
                                    />
                                  </TabPanel>
                                  <TabPanel>
                                    <RenderPropertiesWithToggle
                                      title={`${BRAND_NAME} Properties`}
                                      propsObj={event.xnotify_properties}
                                    />
                                  </TabPanel>
                                </TabPanels>
                              </Tabs>
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    </React.Fragment>
                  ))
                )}
              </Tbody>
            </Table>


            {/* ✅ Pagination + Per Page Dropdown */}
            <Box mt={6} display="flex" justifyContent="center" alignItems="center">


              <Pagination
                activePage={pagination.current_page}
                itemsCountPerPage={pagination.per_page}
                totalItemsCount={pagination.total_events}
                pageRangeDisplayed={10}
                onChange={handlePageChange}
                itemClass="page-item"
                linkClass="page-link"
                activeClass="active"
                activeLinkClass="active-link"
                disabledClass="disabled"
              />
            </Box>
          </>
        )}
      </>}

    </>
  );
};

export default EventsTable;
